import { useState, useEffect, useCallback } from 'react';
import { FaTrash } from 'react-icons/fa';
import { MdFaceRetouchingNatural as FaceIcon } from 'react-icons/md';
import api from '../../api';

export default function Passkeys() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [revokingId, setRevokingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.getAllPasskeys()
      .then(setPasskeys)
      .catch(err => setError(err.message || 'Failed to load passkeys.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleRevoke = async (credentialId, ownerName) => {
    setRevokingId(credentialId);
    setError('');
    try {
      await api.revokePasskey(credentialId);
      setPasskeys(prev => prev.filter(p => p.credential_id !== credentialId));
      setSuccess(`Revoked ${ownerName}'s Face ID.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to revoke passkey.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-10">

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#0c1b4d]">Face ID / Passkeys</h1>
          <p className="text-gray-500 text-sm mt-1">
            Every passkey employees have registered for passwordless sign-in. Registration only
            ever happens from the employee's own device — you can view and revoke here, but not
            register one on someone else's behalf.
          </p>
        </div>

        {error   && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mb-4 bg-green-50 px-4 py-2 rounded-lg">{success}</p>}

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Employee</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Nickname</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Device Type</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Registered</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Last Used</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {passkeys.map((p) => (
                    <tr key={p.credential_id} className="border-b border-gray-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap">
                        <p className="font-medium text-[#0c1b4d] flex items-center gap-1.5">
                          <FaceIcon size={13} className="text-gray-400" /> {p.name}
                        </p>
                        <p className="text-xs text-gray-400">{p.employee_id || '—'}</p>
                      </td>
                      <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{p.nickname}</td>
                      <td className="px-5 py-3 text-gray-500 whitespace-nowrap capitalize">{p.device_type?.replace('_', ' ')}</td>
                      <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-gray-500 whitespace-nowrap">
                        {p.last_used_at ? new Date(p.last_used_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => handleRevoke(p.credential_id, p.name)}
                          disabled={revokingId === p.credential_id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-60 ml-auto"
                        >
                          <FaTrash size={11} /> {revokingId === p.credential_id ? 'Revoking...' : 'Revoke'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {passkeys.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400">No passkeys registered yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
